import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import PersonalSpace from '../../../molecules/PersonalSpace'
import { useChartCatalogActions } from '../hooks/useChartCatalogActions'
import { CHART_CATALOG_BY_ID, resolveCatalogEntry } from '../lib/chartCatalog'
import { usePortfolioData } from './PortfolioDataProvider'
import { useUtilityPanel } from './UtilityPanelProvider'
import { TOOL_REGISTRY } from './tools/toolRegistry'
import AddChartTile from './AddChartTile'
import ChartTile from './ChartTile'

// Vertical stack of chart tiles. Three kinds of entries:
//   - Saved instances from viewConfig.defaultCharts (the user's
//     configured charts -- can include multiple of the same catalog
//     preset, each is a separate row).
//   - The transient tile (single right-click slot at the bottom),
//     currently unused since right-click flows open the exclusive
//     overlay -- kept around for the slot to revive later.
//   - AddChartTile + PersonalSpace as trailing UI affordances.

const useStyles = makeStyles(() => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 14,
    overflow: 'auto',
    flex: 1,
    minHeight: 0
  },
  emptyHint: {
    flexShrink: 0,
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13,
    textAlign: 'center',
    padding: '8px 16px',
    lineHeight: 1.5
  }
}))

export default function ChartStack () {
  const classes = useStyles()
  const { config } = usePortfolioData()
  const { transientTile, closeTransientTile } = useUtilityPanel()
  const { removeChart, updateChart } = useChartCatalogActions()

  // Resolve each saved ChartInstance to the render-time tile shape.
  // The instance stores `{ id, catalogId?, toolId?, payload? }`; the
  // catalog preset (if present) contributes default payload + icon,
  // and the instance's own payload shallow-overrides on top so user
  // edits persist across reloads. Render-time resolution also means
  // catalog edits (new defaults / new icon) take effect on next
  // render without rewriting saved instances.
  const savedTiles = useMemo(() => {
    const instances = config?.defaultCharts || []
    return instances.map((instance) => {
      const catalogId = instance.catalogId || null
      const entry = catalogId ? CHART_CATALOG_BY_ID[catalogId] : null
      const toolId = instance.toolId || entry?.toolId || null
      let catalogPayload = null
      let placeholder = null
      if (catalogId) {
        if (!entry) {
          placeholder = `Chart preset "${catalogId}" no longer available`
        } else {
          const resolved = resolveCatalogEntry(catalogId, { config })
          if (resolved.ok) catalogPayload = resolved.payload
          else placeholder = resolved.reason || 'Chart no longer available'
        }
      }
      // Catalog defaults first, instance overrides win. Tools always
      // see the merged object; they call onPayloadChange (which
      // patches instance.payload only) to persist edits.
      const payload = catalogPayload || instance.payload
        ? { ...(catalogPayload || {}), ...(instance.payload || {}) }
        : null
      return {
        id: instance.id,
        source: 'saved',
        catalogId,
        entry,
        toolId,
        payload,
        placeholder
      }
    })
  }, [config])

  // Transient slot stays in the data flow but is dormant -- right-click
  // flows now open the exclusive overlay instead. Left in so we can
  // revive the slot for "preview before save" workflows.
  const transientForRender = useMemo(() => {
    if (!transientTile) return null
    const entry = transientTile.catalogId ? CHART_CATALOG_BY_ID[transientTile.catalogId] : null
    return {
      id: transientTile.id,
      source: 'transient',
      toolId: transientTile.toolId,
      payload: transientTile.payload,
      catalogId: transientTile.catalogId || null,
      entry,
      placeholder: null
    }
  }, [transientTile])

  const allTiles = useMemo(() => {
    return transientForRender ? [...savedTiles, transientForRender] : savedTiles
  }, [savedTiles, transientForRender])

  return (
    <div className={classes.stack}>
      {allTiles.length === 0 && (
        <div className={classes.emptyHint}>
          No charts in this view yet.
        </div>
      )}
      {allTiles.map((tile) => {
        const toolEntry = tile.toolId ? TOOL_REGISTRY[tile.toolId] : null
        // Tile title is the tool name ("Allocation" / "Trend" /
        // "Compare") rather than the catalog preset name. Presets
        // contribute defaults, not identity -- the user can configure
        // each instance from there. Falls back to a generic label
        // when the tool can't be resolved (broken instance).
        const displayTitle = toolEntry?.label || 'Chart'
        const renderIcon = toolEntry?.renderIcon || null
        // Per-instance payload patcher. Transient tiles can't persist
        // (they're not in defaultCharts), so they get null and the
        // tool falls back to its own local state for that session.
        const onPayloadChange = tile.source === 'saved'
          ? (patch) => updateChart(tile.id, { payload: patch })
          : null
        return (
          <ChartTile
            key={tile.id}
            tile={tile}
            displayTitle={displayTitle}
            renderIcon={renderIcon}
            placeholder={tile.placeholder}
            onClose={tile.source === 'transient' ? closeTransientTile : undefined}
            onRemove={tile.source === 'saved' ? () => removeChart(tile.id) : undefined}
            onPayloadChange={onPayloadChange}
          />
        )
      })}
      <AddChartTile />
      <PersonalSpace size={120} />
    </div>
  )
}
